import { component, OnInit, prop } from '../core'

enum viewportPreferences {
	activeSM = 'gb-active-sm',
	activeMED = 'gb-active-med',
	activeLG = 'gb-active-lg',
}

/**
 * @noInheritDoc
 */
@component('gb-expander')
export class Expander extends HTMLElement implements OnInit {
	expanderButton: Element
	expanderHeadline: HTMLElement
	expanderContent: HTMLElement
	expanderContentBody: HTMLElement
	hasClosedHeadline = false

	/**
	 * maps to the data-disabled attribute.  When the attribute is present, it means the expander
	 * is disabled, which means it is open and not clickable.
	 */
	@prop('boolean')
	dataDisabled: boolean

	@prop('string')
	openHeadline: string

	@prop('string')
	closedHeadline: string

	constructor() {
		super()
	}

	onInit() {
		this.expanderButton = this.querySelector('.gb-expander-btn')
		this.expanderHeadline = this.querySelector('.gb-expander-btn .gb-expander-headline')
		this.expanderContent = this.querySelector('.gb-expander-content')
		this.expanderContentBody = this.querySelector('.gb-expander-content-body')

		this.expanderButton.addEventListener('click', () => this.onClick())
		this.expanderButton.addEventListener('keydown', e => this.handleKeydown(e as KeyboardEvent))

		if (this.closedHeadline && this.openHeadline) this.hasClosedHeadline = true

		// Ensure the correct headline text is shown depending on the expander's current state.
		if (this.hasClosedHeadline) {
			if (!this.isOpen && !this.dataDisabled) {
				this.setHeadlineText(this.closedHeadline)
			} else {
				this.setHeadlineText(this.openHeadline)
			}
		}

		if (this.isOpen) {
			this.expanderContent.setAttribute('aria-hidden', 'false')
		} else {
			this.expanderContent.setAttribute('aria-hidden', 'true')
		}

		// After init, make sure the aria-expanded attribute on the button reflects the open/active-ness
		// of the expander - also set visibility to expander content so keyboard focus does not go inside when closed
		if (!this.dataDisabled && this.expanderButton.hasAttribute('aria-expanded')) {
			this.expanderButton.setAttribute('aria-expanded', this.isOpen.toString())
		}
	}

	/**
	 * Method called when the element is removed from the DOM.  Need to remove event handlers.
	 */
	disconnectedCallback() {
		this.expanderButton.removeEventListener('click', () => this.onClick())
		this.expanderButton.removeEventListener('keydown', e => this.handleKeydown(e as KeyboardEvent))
	}

	/**
	 * Callback to handle keydown events on the expander button.  Expanders should open when the 'SpaceBar''
	 * OR the 'Enter' key is pressed when element is focused.
	 * @param e
	 */
	private handleKeydown(e: KeyboardEvent) {
		if (e.key === ' ' || e.key === 'Enter') {
			e.preventDefault()
			this.onClick()
		}
	}

	/**
	 * Callback for when the element is clicked.
	 * Toggle open or closed if not disabled.
	 */
	private onClick() {
		if (!this.dataDisabled) {
			if (this.isOpen) {
				this.closeExpander()
			} else {
				this.openExpander()
			}
		}
	}

	/**
	 * Returns if the expander is currently open (active) or not
	 */
	public get isOpen() {
		return (
			this.classList.contains('active') || window.getComputedStyle(this.expanderContent).visibility === 'visible'
		)
	}

	/**
	 * Closes the expander
	 */
	public closeExpander() {
		const expanderContentHeight = this.expanderContentBody.offsetHeight

		// Set the height before closing so CSS transition happens
		this.expanderContent.style.height = expanderContentHeight + 'px'

		if (this.hasClosedHeadline) {
			this.setHeadlineText(this.closedHeadline)
		}

		// Workaround to fix a race condition so that the transition animation happens
		setTimeout(() => {
			this.expanderContent.style.height = ''
			this.classList.remove(
				'active',
				viewportPreferences.activeSM,
				viewportPreferences.activeMED,
				viewportPreferences.activeLG
			)
			this.expanderButton.setAttribute('aria-expanded', 'false')
			this.expanderContent.setAttribute('aria-hidden', 'true')
		}, 0)
	}

	/**
	 * Opens the expander
	 */
	public openExpander() {
		const expanderContentHeight = this.expanderContentBody.offsetHeight

		// After CSS transition finishes, unset the static height to stay 'responsive'
		this.expanderContent.addEventListener(
			'transitionend',
			() => {
				this.expanderContent.style.height = ''
			},
			{ once: true }
		)

		if (this.hasClosedHeadline) {
			this.setHeadlineText(this.openHeadline)
		}

		this.classList.add('active')
		this.expanderContent.style.height = expanderContentHeight + 'px'
		this.expanderButton.setAttribute('aria-expanded', 'true')
		this.expanderContent.setAttribute('aria-hidden', 'false')
	}

	public setHeadlineText(headlineText) {
		if (headlineText && this.expanderHeadline) {
			this.expanderHeadline.innerText = headlineText
		}
	}
}
